import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
  Inject,
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { AuthenticationService } from '../../services/authentication.service';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import {
  SimulationResponse,
  SimulationResults,
  SimulationSearchFilters,
} from '../../core/models/simulation.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { filter as filterFunc, first, debounceTime, merge, tap } from 'rxjs';
import { DEFAULT_DEBOUNCE_TIME } from '../../core/consts/consts';
import { FilterOptionsPopupComponent } from '../popups/filter-options-popup/filter-options-popup.component';
import { ConfirmPopupComponent } from '../popups/confirm-popup/confirm-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '../../services/snackbar.service';
import { INITIAL_SIMULATION_FILTER_STATE } from '../../core/consts/initial-simulation-filters.const';
import { getFileNameWithoutExtension } from '../../utils/format.util';
import { sanitizeSearchString } from '../../utils/sanitize.util';

@Component({
  selector: 'app-common-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatTooltipModule,
    MatButtonModule,
    RouterModule,
  ],
  templateUrl: './common-table.component.html',
  styleUrl: './common-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonTableComponent
  extends Destroyable(Object)
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() isSelector = false;
  @Input() label = 'Search simulations';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('searchInput') searchInput!: ElementRef;

  dataSource = new MatTableDataSource<SimulationResults>([]);
  simulations: SimulationResponse<SimulationResults>[] = [];
  searchString: string = '';
  searchByOptionSelected = 'User';
  count = 0;
  filterData: SimulationSearchFilters = JSON.parse(JSON.stringify(INITIAL_SIMULATION_FILTER_STATE));
  isLoading = false;
  currentUserEmail: string = '';

  // Make utility function available to template
  protected readonly getFileNameWithoutExtension = getFileNameWithoutExtension;

  public readonly simulationTypes = [
    'SubsampleKabs',
    'SubsampleFF',
    'SubsamplePtsd',
    'SubsamplePC',
    'SubsampleKr',
    'SubsampleCubeTrendKabs',
    'SubsampleCubeTrendFF',
    'SubsampleN',
    'UpscalingKabs',
    'UpscalingFF',
    'UpscalingPC',
    'UpscalingKr',
    'UpscalingN',
  ];

  public readonly runStatuses = [
    'Success',
    'Failure',
    'Waiting',
    'Initializing',
    'Processing',
    'Canceled',
  ];

  readonly searchByOptions = [
    { label: 'User', value: 'User' },
    { label: 'Rock file', value: 'File' },
    { label: 'Name', value: 'Name' },
  ];

  displayedColumns: string[] = [
    'name',
    'type',
    'directionX',
    'directionY',
    'directionZ',
    'view',
    'download',
    'delete',
    'userEmail',
    'fileName',
    'createdAt',
    'completedAt',
  ];

  constructor(
    private simulationsService: SimulationsService,
    private authenticationService: AuthenticationService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    @Inject(ActivatedRoute) private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeFilterData();
    this.authenticationService.getCurrentUser()
      .pipe(this.takeUntilDestroyed())
      .subscribe(user => {
        this.currentUserEmail = user.email;
        this.cd.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.applySearch());
    this.setupSortAndPaginationChangeListeners();
  }

  isDateField(fieldValue: string): boolean {
    return fieldValue === 'CreatedAt' || fieldValue === 'CompletedAt';
  }

  openFilterPopup(): void {
    const dialogRef = this.dialog.open(FilterOptionsPopupComponent, {
      disableClose: true,
      data: {
        ...this.filterData,
        simTypes: this.simulationTypes,
        statuses: this.runStatuses,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filterFunc((accessRequest) => !!accessRequest),
        this.takeUntilDestroyed(),
      )
      .subscribe((data: SimulationSearchFilters) => {
        this.filterData = data;
        this.applySearch(true);
      });
  }

  public applySearch(resetPage: boolean = false): void {
    if (resetPage && this.paginator) {
      this.paginator.pageIndex = 0;
    }

    const startDateFormatted = this.formatDate(this.filterData.startDate);
    const endDateFormatted = this.formatDate(this.filterData.endDate);

    const selectedTypes =
      this.filterData.types?.filter((type: any) => type.checked).map((type: any) => type.name) ||
      [];

    const selectedStatuses =
      this.filterData.runStatus
        ?.filter((status: any) => status.checked)
        .map((status: any) => status.name) || [];

    this.filterData = {
      ...this.filterData,
      pageSize: this.paginator.pageSize,
      page: this.paginator.pageIndex + 1,
    };

    const filteredData: SimulationSearchFilters = {
      filterType: this.searchByOptionSelected,
      searchString: sanitizeSearchString(this.searchString),
      types: selectedTypes,
      runStatus: selectedStatuses,
      directions: {
        ...this.filterData.directions,
      },
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      page: this.filterData.page,
      pageSize: this.filterData.pageSize,
    };

    this.simulationsService
      .getFilteredSimulations(filteredData)
      .pipe(this.takeUntilDestroyed())
      .subscribe((result) => {
        this.count = result.count;
        this.dataSource.data = result.data;
      });

    this.updateQueryParams(filteredData);
  }

  private initializeFilterData(): void {
    this.route.queryParamMap.pipe(first(), this.takeUntilDestroyed()).subscribe((data) => {
      const term = data.get('SearchString');

      const types = data.getAll('Type');
      const runStatuses = data.getAll('RunStatus');
      const directions = data.getAll('Direction');

      const params = {
        filterType: data.get('FilterBy'),
        sortColumnName: data.get('sortColumnName'),
        sortDirection: data.get('sortDirection'),
        page: Number(data.get('page')),
        pageSize: Number(data.get('pageSize')),
        startDate: data.get('CreatedAt'),
        endDate: data.get('CompletedAt'),
        types: types,
        runStatus: runStatuses,
        directions: directions,
      };

      const allPossibleDirections = ['x', 'y', 'z'];
      const directionsObject: { [key: string]: boolean } = {};

      allPossibleDirections.forEach((direction) => {
        directionsObject[direction] = directions.includes(direction);
      });

      const parsedFilterData = {
        searchString: sanitizeSearchString(term) || this.filterData.searchString,
        filterType: params.filterType || this.filterData.filterType,
        page: params.page || this.filterData.page,
        pageSize: params.pageSize || this.filterData.pageSize,
        startDate: params.startDate || this.filterData.startDate,
        endDate: params.endDate || this.filterData.endDate,
        types: params.types.length > 0 ? params.types : this.filterData.types,
        runStatus: params.runStatus.length > 0 ? params.runStatus : this.filterData.runStatus,
        directions: directionsObject,
      };

      this.filterData = parsedFilterData;
    });

    this.cd.detectChanges();
  }

  //TODO: temporary disabled polling because of filtering. Need further discussions

  // private startStatusPolling(): void {
  //   interval(DEFAULT_DELAY_TIME)
  //     .pipe(
  //       startWith(0),
  //       this.takeUntilDestroyed(),
  //       switchMap(() => this.simulationsService.getAllSimulations()),
  //     )
  //     .subscribe({
  //       next: (data) => {
  //         this.simulations = data;
  //         this.dataSource.data = this.simulations;
  //       },
  //     });
  // }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onSearchByChanged(option: string): void {
    this.searchString = '';
    this.dataSource.filter = '';

    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  downloadResult(simulation: SimulationResponse<SimulationResults>): void {
    if (simulation) {
      this.simulationsService.downloadResult(simulation.id);
    }
  }

  resultsUnavailable(simulation: SimulationResponse<SimulationResults>): boolean {
    return simulation.simulationStatus !== 'Finished' || simulation.results?.every(result => result.status !== 'Success');
  }

  getResultsRouterLink(simulation: SimulationResponse<SimulationResults>): string[] {
    switch (simulation.type) {
      case 'SubsampleKabs':
      case 'UpscalingKabs':
        return ['/simulation-results/kabs', simulation.id];

      case 'SubsamplePtsd':
        return ['/simulation-results/ptsd', simulation.id];

      case 'SubsampleFF':
      case 'UpscalingFF':
        return ['/simulation-results/form-factor', simulation.id];

      case 'SubsamplePC':
      case 'UpscalingPC':
        return ['/simulation-results/pc', simulation.id];

      case 'SubsampleKr':
      case 'UpscalingKr':
        return ['/simulation-results/kr', simulation.id];

      case 'SubsampleN':
      case 'UpscalingN':
        return ['/simulation-results/n', simulation.id];

      case 'SubsampleCubeTrendFF':
        return ['/simulation-results/cube-trend/formation-factor', simulation.id];

      case 'SubsampleCubeTrendKabs':
        return ['/simulation-results/cube-trend/absolute-permeability', simulation.id];

      default:
        return ['/simulation-search'];
    }
  }

  goToResults(simulation: SimulationResponse<SimulationResults>): void {
    this.navigateToSimulationDetails(simulation);
  }

  navigateToSimulationDetails(simulation: SimulationResponse<SimulationResults>): void {
    const route = this.getResultsRouterLink(simulation);
    this.router.navigate(route);
  }

  openResultsInNewTab(simulation: SimulationResponse<SimulationResults>): void {
    const route = this.getResultsRouterLink(simulation);
    const url = this.router.serializeUrl(this.router.createUrlTree(route));
    window.open(url, '_blank');
  }

  getDirectionStatus(
    simulation: SimulationResponse<SimulationResults>,
    direction: 'x' | 'y' | 'z',
  ): string {
    return simulation.results?.find((r) => r.direction.toLowerCase() === direction)?.status || '—';
  }

  isDeleteDisabled(simulation: SimulationResponse<SimulationResults>): boolean {
    const isRunning = simulation.simulationStatus !== 'Finished';
    const isNotOwner = simulation.userEmail !== this.currentUserEmail;
    return isRunning || isNotOwner;
  }

  deleteSimulation(simulation: SimulationResponse<SimulationResults>): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      disableClose: true,
      data: {
        title: 'Delete Simulation',
        message: `Are you sure you want to delete ${simulation.name}? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      },
    });

    dialogRef.afterClosed()
      .pipe(
        filterFunc((confirmed) => !!confirmed),
        this.takeUntilDestroyed(),
      )
      .subscribe(() => {
        const simulationName = simulation.name;
        this.simulationsService.deleteSimulation(simulation.id)
          .pipe(this.takeUntilDestroyed())
          .subscribe(() => {
            this.applySearch();
            this.snackbarService.openCustom({
              message: `${simulationName} was successfully deleted`,
              type: 'success'
            });
          });
      });
  }

  private updateQueryParams(data: SimulationSearchFilters): void {
    const urlTree = this.router.parseUrl(this.router.url);

    urlTree.queryParams['SearchString'] = data.searchString ?? '';
    urlTree.queryParams['FilterBy'] = data.filterType;
    urlTree.queryParams['CreatedAt'] = data.startDate ?? '';
    urlTree.queryParams['CompletedAt'] = data.endDate ?? '';
    urlTree.queryParams['page'] = data.page;
    urlTree.queryParams['pageSize'] = data.pageSize;

    if (data.types && data.types.length > 0) {
      urlTree.queryParams['Type'] = data.types;
    }

    if (data.runStatus && data.runStatus.length > 0) {
      urlTree.queryParams['RunStatus'] = data.runStatus;
    }

    const trueDirections = Object.entries(data.directions)
      .filter(([key, value]) => value)
      .map(([key]) => key);

    if (trueDirections.length > 0) {
      urlTree.queryParams['Direction'] = trueDirections;
    }

    this.router.navigateByUrl(urlTree);
  }

  private formatDate(dateString: string | null): string {
    let value = '';
    if (dateString) {
      const date = new Date(dateString);

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      value = `${day}-${month}-${year}`;
    }

    return value;
  }

  private setupSortAndPaginationChangeListeners(): void {
    const sortChange$ = this.sort.sortChange.pipe(
      tap(() => {
        this.paginator.pageIndex = 0;
        this.filterData = {
          ...this.filterData,
          page: this.paginator.pageIndex + 1,
        };
      }),
    );
    const pageChange$ = this.paginator.page.pipe(debounceTime(DEFAULT_DEBOUNCE_TIME));

    merge(sortChange$, pageChange$)
      .pipe(
        tap(() => {
          this.applySearch();
        }),
      )
      .subscribe();
  }
}
